// Tạo / sửa bảng hoa hồng CTV theo số đơn (§2.5, D2, D4, D25).
//  · Bậc bắt đầu từ 1, liền nhau, bậc cuối mở; mức là số nguyên ≥ 0 và không giảm dần.
//  · Sửa = phiên bản mới, áp dụng từ tháng sau. Chỉ giảm mức (so với bản ĐANG ÁP tháng này, không
//    phải bản đã hẹn) thì được chọn áp ngay. Bản mới thay mọi bản đã hẹn trước đó.
//  · Lưu bị chặn nếu mức cao nhất vượt chênh lệch nhỏ nhất của bạn trên một CTV đang dùng bảng.

import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { AlertTriangle, Plus, Trash2, Users } from 'lucide-react'
import { validateScheme } from '@shoptrack/common/commission-scheme'
import { post, put } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MoneyInput } from '@/components/ui/money-input'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { errorDetails, errorMessage } from '@/components/price-lists/types'
import {
  monthLabel,
  scheduledVersion,
  shiftMonth,
  versionAt,
  vnd,
  type CapCheck,
  type CommissionMode,
  type CommissionTier,
  type Scheme,
} from './types'

interface TierDraft {
  toOrders: number | null
  amountPerOrder: number
}

const DEFAULT_TIERS: TierDraft[] = [
  { toOrders: 100, amountPerOrder: 300 },
  { toOrders: 300, amountPerOrder: 500 },
  { toOrders: null, amountPerOrder: 700 },
]

const MAX_AMOUNT = 1_000_000

function toTiers(drafts: TierDraft[]): CommissionTier[] {
  let from = 1
  return drafts.map((t, i) => {
    const last = i === drafts.length - 1
    const tier: CommissionTier = { fromOrders: from, toOrders: last ? null : t.toOrders, amountPerOrder: t.amountPerOrder }
    from = (t.toOrders ?? from) + 1
    return tier
  })
}

export type SchemeSaved = { kind: 'created'; id: string; code: string; name: string } | { kind: 'updated'; code: string; effectiveFromMonth: string | null; version: number }

export function SchemeFormDialog({
  scheme,
  inUse,
  month,
  onClose,
  onSaved,
}: {
  /** null = tạo mới */
  scheme: Scheme | null
  /** Số CTV đang dùng bảng (khi sửa). */
  inUse: number
  /** Tháng hiện tại (YYYY-MM). */
  month: string
  onClose: () => void
  onSaved: (r: SchemeSaved) => void
}) {
  const originalDrafts = useMemo<TierDraft[]>(
    () => (scheme ? scheme.tiers.map((t) => ({ toOrders: t.toOrders, amountPerOrder: t.amountPerOrder })) : DEFAULT_TIERS),
    [scheme],
  )
  const [name, setName] = useState(scheme?.name ?? '')
  const mode: CommissionMode = 'progressive'
  const [drafts, setDrafts] = useState<TierDraft[]>(originalDrafts)
  const [applyNow, setApplyNow] = useState(false)
  const [touched, setTouched] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cap, setCap] = useState<CapCheck | null>(null)

  const tiers = toTiers(drafts)
  const issues = [
    ...validateScheme({ mode, tiers }),
    ...tiers.flatMap((t, i) => (t.amountPerOrder > MAX_AMOUNT ? [`Bậc ${i + 1}: chiết khấu tối đa ${vnd(MAX_AMOUNT)} mỗi đơn`] : [])),
  ]

  const original = scheme ? toTiers(originalDrafts) : null
  const defChanged = !!scheme && (mode !== scheme.mode || JSON.stringify(tiers) !== JSON.stringify(original))
  const nameChanged = name.trim() !== (scheme?.name ?? '')
  // Mức đang áp tháng này (có thể khác bản mới nhất khi đã hẹn bản tháng sau) — mốc so "chỉ giảm".
  const inForce = scheme ? versionAt(scheme, month) : null
  const scheduled = scheme ? scheduledVersion(scheme, month) : null
  // Áp ngay chỉ khi giữ nguyên chế độ + mốc bậc và không bậc nào tăng so với bản đang áp (máy chủ kiểm lại).
  const decreaseOnly =
    !!inForce &&
    mode === inForce.mode &&
    tiers.length === inForce.tiers.length &&
    tiers.every((t, i) => t.fromOrders === inForce.tiers[i]!.fromOrders && t.toOrders === inForce.tiers[i]!.toOrders && t.amountPerOrder <= inForce.tiers[i]!.amountPerOrder)
  const nextMonth = shiftMonth(month, 1)
  const willApplyNow = applyNow && decreaseOnly

  const save = useMutation({
    mutationFn: async (): Promise<SchemeSaved> => {
      const def = { mode, tiers }
      if (!scheme) {
        const r = await post<{ scheme: { id: string; code: string; name: string } }>('/commissions/schemes', { name: name.trim(), def })
        return { kind: 'created', id: r.scheme.id, code: r.scheme.code, name: r.scheme.name }
      }
      const r = await put<{ effectiveFromMonth: string | null; version: number }>(`/commissions/schemes/${scheme.id}`, {
        ...(nameChanged ? { name: name.trim() } : {}),
        ...(defChanged ? { def, applyNow: willApplyNow } : {}),
      })
      return { kind: 'updated', code: scheme.code, ...r }
    },
    onSuccess: (r) => onSaved(r),
    onError: (e) => {
      const c = errorDetails<CapCheck>(e, 'commission.cap_exceeded')
      if (c && typeof c.maxRate === 'number') {
        setCap(c)
        setError(null)
        return
      }
      if (errorDetails(e, 'commission.apply_next_month')) {
        setApplyNow(false)
        setError('Thay đổi này sẽ áp dụng từ tháng sau — bấm Lưu lần nữa.')
        return
      }
      setError(errorMessage(e, 'Không lưu được bảng chiết khấu'))
    },
  })

  function submit() {
    setTouched(true)
    setError(null)
    setCap(null)
    if (!name.trim()) return setError('Vui lòng đặt tên bảng chiết khấu')
    if (issues.length) return setError('Bảng chưa hợp lệ')
    if (scheme && !defChanged && !nameChanged) return setError('Chưa có thay đổi nào để lưu')
    save.mutate()
  }

  // Sửa gì thì bỏ thông báo lỗi cũ (lỗi còn đúng sẽ hiện lại khi bấm Lưu).
  function edited() {
    setCap(null)
    setError(null)
  }

  function updateDraft(i: number, patch: Partial<TierDraft>) {
    setDrafts((d) => d.map((t, j) => (j === i ? { ...t, ...patch } : t)))
    edited()
  }

  function addTier() {
    setDrafts((d) => {
      const all = toTiers(d)
      const last = all[all.length - 1]!
      const head = d.slice(0, -1)
      const lastDraft = d[d.length - 1]!
      return [...head, { ...lastDraft, toOrders: last.fromOrders + 99 }, { toOrders: null, amountPerOrder: lastDraft.amountPerOrder }]
    })
    edited()
  }

  function removeTier(i: number) {
    setDrafts((d) => d.filter((_, j) => j !== i))
    edited()
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-2xl" onInteractOutside={(e) => e.preventDefault()} aria-describedby={undefined}>
        <DialogHeader className="pr-8">
          <DialogTitle>{scheme ? `Sửa bảng ${scheme.code}` : 'Tạo bảng chiết khấu'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <label className="block space-y-1.5">
            <span className="text-sm text-inkSoft">Tên bảng</span>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setError(null)
              }}
              maxLength={100}
              placeholder="VD: CTV tiêu chuẩn"
            />
          </label>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-inkSoft">Các bậc</p>
              <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={addTier} disabled={drafts.length >= 20}>
                <Plus className="size-4" /> Thêm bậc
              </Button>
            </div>
            <div className="overflow-hidden rounded-lg border border-rule">
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)_2.75rem] items-center gap-2 bg-paperSoft px-3 py-2 text-xs font-medium text-inkSoft">
                <span>Từ đơn thứ</span>
                <span>Đến đơn thứ</span>
                <span className="text-right">Chiết khấu mỗi đơn</span>
                <span className="sr-only">Xóa</span>
              </div>
              <ul className="divide-y divide-rule">
                {tiers.map((t, i) => {
                  const last = i === tiers.length - 1
                  return (
                    <li key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)_2.75rem] items-center gap-2 px-3 py-2">
                      <span className="tabular-nums text-ink">{t.fromOrders.toLocaleString('vi-VN')}</span>
                      {last ? (
                        <span className="text-sm text-muted">trở lên</span>
                      ) : (
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={t.fromOrders}
                          step={1}
                          aria-label={`Bậc ${i + 1}: đến đơn thứ`}
                          value={drafts[i]!.toOrders ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? null : Math.floor(Number(e.target.value))
                            updateDraft(i, { toOrders: v != null && Number.isFinite(v) ? v : null })
                          }}
                          className="tabular-nums"
                        />
                      )}
                      <MoneyInput
                        aria-label={`Bậc ${i + 1}: chiết khấu mỗi đơn`}
                        value={drafts[i]!.amountPerOrder}
                        onValueChange={(v) => drafts[i]!.amountPerOrder !== v && updateDraft(i, { amountPerOrder: v })}
                        suffix="đ"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-muted hover:text-danger"
                        onClick={() => removeTier(i)}
                        disabled={tiers.length <= 1}
                        aria-label={`Xóa bậc ${i + 1}`}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </li>
                  )
                })}
              </ul>
            </div>
            {touched && issues.length > 0 && (
              <ul className="list-disc space-y-0.5 rounded-lg bg-danger/5 py-2 pl-7 pr-3 text-xs text-danger">
                {issues.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            )}
          </div>

          {scheme && (
            <div className={cn('flex items-start gap-2 rounded-lg p-3 text-sm', inUse > 0 ? 'bg-warn/10 text-ink' : 'bg-paperSoft text-inkSoft')}>
              <Users className="mt-0.5 size-4 shrink-0" />
              <div className="space-y-1">
                <p>
                  {inUse > 0 ? `${inUse} CTV đang dùng bảng này. ` : 'Chưa CTV nào dùng bảng này. '}
                  {willApplyNow
                    ? `Sửa mức tạo phiên bản v${scheme.version + 1}, áp dụng ngay từ tháng ${monthLabel(month)}.`
                    : `Sửa mức tạo phiên bản v${scheme.version + 1}, áp dụng từ tháng ${monthLabel(nextMonth)}.`}
                </p>
                {inForce && scheduled && (
                  <p className="text-xs text-inkSoft">
                    Bản v{scheduled.version} đã hẹn từ tháng {monthLabel(scheduled.effectiveFromMonth)} sẽ được thay bằng bản mới.
                  </p>
                )}
              </div>
            </div>
          )}

          {scheme && defChanged && decreaseOnly && (
            <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-rule p-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 size-4 accent-accent"
                checked={applyNow}
                onChange={(e) => {
                  setApplyNow(e.target.checked)
                  setError(null)
                }}
              />
              <span>
                <span className="block font-medium text-ink">Áp dụng ngay từ tháng {monthLabel(month)}</span>
              </span>
            </label>
          )}

          {cap && (
            <div className="rounded-lg bg-danger/5 p-3 text-sm text-danger">
              <p className="font-medium">
                Không lưu được: mức cao nhất {vnd(cap.maxRate)}/đơn vượt chênh lệch nhỏ nhất {vnd(cap.minMargin)} của bạn{cap.at ? ` (ô ${cap.at})` : ''} trên một CTV đang dùng bảng.
              </p>
            </div>
          )}

          {error && (
            <p className="flex items-start gap-2 rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Hủy
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {save.isPending ? 'Đang lưu…' : scheme ? 'Lưu' : 'Tạo bảng'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
